import {
  Product,
  ProductDesign,
  ProductInfoComponent,
  ProductType
} from '@inter-ikea-storageone/range';
import {
  ComponentIds,
  Entity,
  GuestConnectionsComponent,
  HierarchyUtils,
  Size
} from '@inter-ikea-core/core';
import { CommunicationSizeComponent, RoomConnectorConstants } from '@inter-ikea-core/core';
import { getMajorityOfArray } from 'rangeConfig/shared/utils/colorUtils/utils';

const getFrontsInScene = (entitiesInScene: Entity[]) => {
  return entitiesInScene.filter(entity => {
    const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return (
      productType === ProductType.SMASTAD_HINGE_DOOR_ASM ||
      productType === ProductType.SMASTAD_DRAWER_ASM
    );
  });
};

export const getSize = (entity: Entity) => {
  return entity.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
};

export const getEntitiesInSceneWithProductTypes = (
  entites: Entity[],
  productTypes: ProductType[]
) => {
  return entites.filter(entity => {
    const productType =
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType || '';
    return productTypes.includes(productType as ProductType);
  });
};

const isFloorStandingFrame = (entity: Entity) => {
  if (!entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    return false;
  }
  const component = entity.getComponent<GuestConnectionsComponent>(
    ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID
  );
  return (
    component?.highestPriorityConnection &&
    RoomConnectorConstants.hasFloorPlacement(component.highestPriorityConnection)
  );
};

export const matchFrontTypeToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.SMASTAD_FRAME
  );

  if (frames.length === 0) {
    return products;
  }

  const frontsInScene = getFrontsInScene(entitiesInScene);

  if (frontsInScene.length === 0) {
    //If we don't have any doors/fronts in the scene don't include drawers in the list
    return products.filter(p => p.type !== ProductType.SMASTAD_DRAWER_ASM);
  }

  const frontTypesOnFrame = frontsInScene
    .map(front => front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType)
    .filter((type): type is ProductType => type !== undefined);

  return products.filter(product => frontTypesOnFrame.includes(product.type));
};

export const matchFrontSizeToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.SMASTAD_FRAME
  );

  const framesWithoutFronts = frames.filter(
    frame =>
      !HierarchyUtils.children(frame).some(child => {
        const childProductType = child.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
          ?.productType;
        return (
          childProductType === ProductType.SMASTAD_HINGE_DOOR_ASM ||
          childProductType === ProductType.SMASTAD_DRAWER_ASM
        );
      })
  );
  const frontsInScene = getFrontsInScene(entitiesInScene);

  if (frontsInScene.length === 0) {
    return products;
  }

  const frontSizesOnFrames = frontsInScene
    .map(front => getSize(front))
    .filter((size): size is Size => size !== undefined);
  const defaultDoorSizesOnFramesWithoutFronts = framesWithoutFronts
    .map(frame => getSize(frame))
    .filter((size): size is Size => size !== undefined)
    .map(size => ({ width: 300, height: size.height }));

  return products.filter(
    product =>
      frontSizesOnFrames.some(
        size =>
          size.width === product.communicatedDimensions.width &&
          size.height === product.communicatedDimensions.height
      ) ||
      defaultDoorSizesOnFramesWithoutFronts.some(
        size =>
          size.width === product.communicatedDimensions.width &&
          size.height === product.communicatedDimensions.height
      )
  );
};

export const matchInteriorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const regularFramesInScene = getEntitiesInSceneWithProductTypes(entitiesInScene, [
    ProductType.SMASTAD_FRAME,
    ProductType.SMASTAD_FRAME_NA,
    ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM
  ]);
  const deskChangingTablesInScene = getEntitiesInSceneWithProductTypes(entitiesInScene, [
    ProductType.SMASTAD_DESK,
    ProductType.SMASTAD_CHANGING_TABLE
  ]);
  const lowPullOutFramesInScene = getEntitiesInSceneWithProductTypes(entitiesInScene, [
    ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM
  ]);

  if (
    [...regularFramesInScene, ...deskChangingTablesInScene, ...lowPullOutFramesInScene].length === 0
  ) {
    return products;
  }
  const regularFrameDepthsInScene = regularFramesInScene.map(entity => getSize(entity)?.depth);
  const regularFrameWidthsInScene = regularFramesInScene.map(entity => getSize(entity)?.width);

  return products.filter(product => {
    if (product.type === ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM) {
      return regularFrameDepthsInScene.includes(product.communicatedDimensions.depth);
    } else if (
      product.communicatedDimensions.width === 300 &&
      product.communicatedDimensions.depth === 550
    ) {
      return deskChangingTablesInScene.length > 0 || lowPullOutFramesInScene.length > 0;
    } else if (
      product.communicatedDimensions.width === 600 &&
      product.communicatedDimensions.depth === 550 &&
      product.type !== ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM &&
      product.type !== ProductType.SMASTAD_CLOTH_RAIL_ASM
    ) {
      return (
        deskChangingTablesInScene.length > 0 ||
        (regularFrameDepthsInScene.includes(product.communicatedDimensions.depth) &&
          regularFrameWidthsInScene.includes(product.communicatedDimensions.width))
      );
    }
    return (
      regularFrameDepthsInScene.includes(product.communicatedDimensions.depth) &&
      regularFrameWidthsInScene.includes(product.communicatedDimensions.width)
    );
  });
};

/**
 * Don't diplay wire baskets if there are no floor standing frames in scene
 **/
export const matchInteriorsToFramePlacement = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(e =>
    e.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (framesInScene.length === 0) {
    return products;
  }
  if (!framesInScene.some(frame => isFloorStandingFrame(frame))) {
    return products.filter(product => product.type !== ProductType.SMASTAD_WIRE_BASKET_ASM);
  }
  return products;
};

export const matchBenchAssemblyToFrontsInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  const frontsInScene = entitiesInScene.filter(
    entity =>
      entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
      entity.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
  );
  const frontDesigns = frontsInScene
    .map(front => front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design)
    .filter(Boolean) as ProductDesign[];
  const designForBenchAssembly =
    frontDesigns.length > 0 ? getMajorityOfArray(frontDesigns) : ProductDesign.WHITE;

  return products.filter(product =>
    product.type === ProductType.SMASTAD_BENCH_WITH_DRAWER_ASM
      ? product.design === designForBenchAssembly
      : true
  );
};

export const matchAccessoriesToEntitiesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  if (entitiesInScene.length === 0) return products;
  const clothRails55InScene =
    entitiesInScene.filter(
      e =>
        (e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
          ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM ||
          e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
            ProductType.SMASTAD_CLOTH_RAIL_ASM) &&
        e.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.depth === 550
    ).length > 0;

  const benchInScene =
    entitiesInScene.filter(
      e =>
        e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
        ProductType.SMASTAD_BENCH
    ).length > 0;

  return products.filter(product => {
    if (product.type === ProductType.SMASTAD_HANGING_STORAGE_CASE) {
      return clothRails55InScene;
    } else if (product.type === ProductType.SMASTAD_BENCH_PAD) {
      return benchInScene;
    }
    return true;
  });
};
